import { useCallback, useMemo } from 'react'
import { CreditCard, Crown, FileClock, KeyRound, ScrollText, ServerCog, ShieldAlert } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import {
  fetchAbuse,
  fetchVipLimits,
  fetchRotationLog,
  fetchServiceKeys,
  fetchProviderKeys,
  fetchKeyPolicy,
  fetchKeyAudit,
  DEVX_STR as S,
  type AbuseRow,
  type VipLimitRow,
  type RotationLogRow,
  type ServiceKeyRow,
  type ProviderKeyRow,
  type KeyPolicyRow,
  type KeyAuditRow,
} from './devApiExt'

/**
 * A13.06 — Suiiste'mol aniqlash. `/admin/developers/abuse` greenfield → fail-soft.
 */
export function AbuseDetectionScreen() {
  const columns = useMemo<AdminColumn<AbuseRow>[]>(
    () => [
      { key: 'app', header: S.name, type: 'text' },
      { key: 'pattern', header: S.pattern, type: 'text' },
      { key: 'severity', header: S.severity, type: 'text' },
      { key: 'requests', header: S.requests, type: 'number' },
      { key: 'detected_at', header: S.detectedAt, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
    ],
    [],
  )
  const fetcher = useCallback(fetchAbuse, [])
  return (
    <>
      <PageHeader title={S.abuse} screenId="A13.06" />
      <DataTable<AbuseRow>
        screenId="A13.06"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <ShieldAlert size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/**
 * A13.10 — VIP limitlar. `/admin/developers/vip-limits` greenfield → fail-soft.
 */
export function VipLimitsScreen() {
  const columns = useMemo<AdminColumn<VipLimitRow>[]>(
    () => [
      { key: 'app', header: S.name, type: 'text' },
      { key: 'owner', header: S.owner, type: 'text' },
      { key: 'per_minute', header: S.perMinute, type: 'number' },
      { key: 'per_day', header: S.perDay, type: 'number' },
      { key: 'note', header: S.note, type: 'text' },
    ],
    [],
  )
  const fetcher = useCallback(fetchVipLimits, [])
  return (
    <>
      <PageHeader title={S.vipLimits} screenId="A13.10" />
      <DataTable<VipLimitRow>
        screenId="A13.10"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Crown size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/**
 * A13.11 — Kalit rotatsiyasi jurnali. `/admin/developers/rotation-log` greenfield.
 */
export function RotationLogScreen() {
  const columns = useMemo<AdminColumn<RotationLogRow>[]>(
    () => [
      { key: 'key_ref', header: S.keyRef, type: 'text' },
      { key: 'kind', header: S.kind, type: 'text' },
      { key: 'rotated_by', header: S.rotatedBy, type: 'text' },
      { key: 'rotated_at', header: S.rotatedAt, type: 'text' },
      { key: 'reason', header: S.reason, type: 'text' },
    ],
    [],
  )
  const fetcher = useCallback(fetchRotationLog, [])
  return (
    <>
      <PageHeader title={S.rotationLog} screenId="A13.11" />
      <DataTable<RotationLogRow>
        screenId="A13.11"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <FileClock size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/**
 * A13.12 — Ichki servis kalitlari. `/admin/developers/service-keys` greenfield.
 */
export function ServiceKeysScreen() {
  const columns = useMemo<AdminColumn<ServiceKeyRow>[]>(
    () => [
      { key: 'service', header: S.service, type: 'text' },
      { key: 'key_masked', header: S.key, type: 'text' },
      { key: 'scope', header: S.scope, type: 'text' },
      { key: 'last_used', header: S.lastUsed, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
    ],
    [],
  )
  const fetcher = useCallback(fetchServiceKeys, [])
  return (
    <>
      <PageHeader title={S.serviceKeys} screenId="A13.12" />
      <DataTable<ServiceKeyRow>
        screenId="A13.12"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <ServerCog size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/**
 * A13.17 — Provayder kalitlari (to'lov). `/admin/developers/provider-keys` greenfield.
 */
export function ProviderKeysScreen() {
  const columns = useMemo<AdminColumn<ProviderKeyRow>[]>(
    () => [
      { key: 'provider', header: S.provider, type: 'text' },
      { key: 'environment', header: S.environment, type: 'text' },
      { key: 'key_masked', header: S.key, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'updated_at', header: S.updated, type: 'text' },
    ],
    [],
  )
  const fetcher = useCallback(fetchProviderKeys, [])
  return (
    <>
      <PageHeader title={S.providerKeys} screenId="A13.17" />
      <DataTable<ProviderKeyRow>
        screenId="A13.17"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <CreditCard size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/**
 * A13.19 — Kalit siyosati (muddat, murakkablik). `/admin/developers/key-policy`
 * greenfield → fail-soft.
 */
export function KeyPolicyScreen() {
  const columns = useMemo<AdminColumn<KeyPolicyRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'scope', header: S.scope, type: 'text' },
      { key: 'max_age_days', header: S.maxAge, type: 'number' },
      { key: 'min_length', header: S.minLength, type: 'number' },
      { key: 'rotation_days', header: S.rotationDays, type: 'number' },
      { key: 'enforced', header: S.enforced, type: 'bool' },
    ],
    [],
  )
  const fetcher = useCallback(fetchKeyPolicy, [])
  return (
    <>
      <PageHeader title={S.keyPolicy} screenId="A13.19" />
      <DataTable<KeyPolicyRow>
        screenId="A13.19"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <ScrollText size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/**
 * A13.20 — Kalit auditi. `/admin/developers/key-audit` greenfield → fail-soft.
 */
export function KeyAuditScreen() {
  const columns = useMemo<AdminColumn<KeyAuditRow>[]>(
    () => [
      { key: 'actor', header: S.actor, type: 'text' },
      { key: 'action', header: S.action, type: 'text' },
      { key: 'key_ref', header: S.keyRef, type: 'text' },
      { key: 'ip', header: S.ip, type: 'text' },
      { key: 'at', header: S.at, type: 'text' },
    ],
    [],
  )
  const fetcher = useCallback(fetchKeyAudit, [])
  return (
    <>
      <PageHeader title={S.keyAudit} screenId="A13.20" />
      <DataTable<KeyAuditRow>
        screenId="A13.20"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <KeyRound size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
